import state from '../store';
import { CartItem, CartPart, Part, Product } from '../types';

const getRutPrice = (price: number) => {
  return Math.ceil(price / 2);
};

const getRotPrice = (price: number) => {
  return Math.ceil(price / 2);
};

const getGreenPrice = (price: number) => {
  return Math.ceil(price * 0.5);
};

const getRutStartFee = () => {
  if (!state.options) return 448;
  if (state.rut) {
    return getRutPrice(state.options.start_fee);
  } else {
    return state.options.start_fee;
  }
};

const getRotStartFee = () => {
  if (!state.options) return 448;
  if (state.rot) {
    return getRotPrice(state.options.start_fee);
  } else {
    return state.options.start_fee;
  }
};

const getProductPrice = (product: Product | CartItem, price?: number, amount: number = 1) => {
  if (product.rot && state.rot) {
    return getRotPrice((price || product.price) * amount);
  } else if (product.rut && state.rut) {
    return getRutPrice((price || product.price) * amount);
  } else if (product.green && state.green) {
    return getGreenPrice((price || product.price) * amount);
  } else {
    return (price || product.price) * amount;
  }
};

const getProductPriceWithRotAndRut = (product: Product) => {
  if (product.rot) {
    return getRotPrice(product.price);
  } else if (product.rut && state.rut) {
    return getRutPrice(product.price);
  } else {
    return product.price;
  }
};

const getPartPrice = (part: Part | CartPart, product: Product | CartItem, amount: number = 1) => {
  if (product.rot && state.rot) {
    return getRotPrice(part.price * amount);
  } else if (product.rut && state.rut) {
    return getRutPrice(part.price * amount);
  } else if (product.green && state.green) {
    return getGreenPrice(part.price * amount);
  } else {
    return part.price * amount;
  }
};


const groupCartItemsByStartFee = (cart: CartItem[]) => {
  return cart.reduce((groups, item) => {
    const startFeeID = item.start_fee_obj?.ID || null;
    if (!groups[startFeeID]) {
      groups[startFeeID] = [];
    }
    groups[startFeeID].push(item);
    return groups;
  }, {} as Record<string, CartItem[]>);
};

const calculateStartFee = (cart: CartItem[] = state.cart) => {
  const uniqueStartFees = new Set<number>();
  let totalRut = 0;
  let totalRot = 0;
  let totalGreen = 0;
  let totalDefault = 0;

  cart.forEach(item => {
    const startFee = item.start_fee_obj;
    if (startFee && !uniqueStartFees.has(startFee.ID)) {
      uniqueStartFees.add(startFee.ID);

      if (item.rut) {
        totalRut += state.rut ? getRutPrice(startFee.price) : startFee.price;
      }
      if (item.rot) {
        totalRot += state.rot ? getRotPrice(startFee.price) : startFee.price;
      }
      if (item.green) {
        totalGreen += state.green ? getGreenPrice(startFee.price) : startFee.price;
      }
      if (!item.rut && !item.rot && !item.green) {
        totalDefault += startFee.price;
      }
    }
  });

  const appliedDiscounts = [totalRut > 0, totalRot > 0, totalGreen > 0];
  return {
    rut: totalRut,
    rot: totalRot,
    green: totalGreen,
    default: totalDefault,
    length: appliedDiscounts.filter(Boolean).length,
  };
};

const getStartFee = () => {
  if (!state.cart) return { rut: 0, rot: 0, green: 0 };
  const hasRut = state.cart.some(item => item.rut && item.start_fee);
  const hasRot = state.cart.some(item => item.rot && item.start_fee);
  const hasGreen = state.cart.some(item => item.green && item.start_fee);
  const rutPrice = state.rut ? getRutPrice(state.options.start_fee) : state.options.start_fee * 1;
  const rotPrice = state.rot ? getRotPrice(state.options.start_fee) : state.options.start_fee * 1;
  const greenPrice = state.green ? getGreenPrice(state.options.start_fee) : state.options.start_fee * 1;
  return { rut: hasRut ? rutPrice : 0, rot: hasRot ? rotPrice : 0, green: hasGreen ? greenPrice : 0, length: [hasRut, hasRot, hasGreen].filter(d => d).length };
};

const getItemPrice = (item: CartItem) => {
  const partsPrice = item.parts.reduce((acc, curr) => {
    return acc + curr.price * curr.amount;
  }, 0);
  return getProductPrice(item, item.price * item.amount + partsPrice);
};

const calculateRot = () => {
  const totalWithRot =
    state.cart.reduce((acc, curr) => {
      return curr.rot ? acc + getItemPrice(curr) : acc;
    }, 0) + (state.cart.some(item => item.rot) ? calculateStartFee().rot : 0);

  const totalWithoutRot = state.cart.reduce((acc, curr) => {
    const partsPrice = curr.parts.reduce((acc, part) => {
      return acc + part.price * part.amount;
    }, 0);
    const startFee = curr.start_fee_obj?.price || 0;

    if (curr.rot) {
      if (!acc.startFeeApplied) {
        acc.startFeeApplied = true;
        acc.amount += curr.price * curr.amount + partsPrice + startFee;
      } else {
        acc.amount += curr.price * curr.amount + partsPrice;
      }
    }
    
    return acc;
  }, { amount: 0, startFeeApplied: false }).amount;

  return totalWithoutRot - totalWithRot;
};

const calculateGreenDiscount = () => {
  const totalWithGreen =
    state.cart.reduce((acc, curr) => {
      return curr.green ? acc + getItemPrice(curr) : acc;
    }, 0) + (state.cart.some(item => item.green) ? calculateStartFee().green : 0);

  const totalWithoutGreen = state.cart.reduce((acc, curr) => {
    const partsPrice = curr.parts.reduce((acc, part) => {
      return acc + part.price * part.amount;
    }, 0);
    const startFee = curr.start_fee_obj?.price || 0;

    if (curr.green) {
      if (!acc.startFeeApplied) {
        acc.startFeeApplied = true;
        acc.amount += curr.price * curr.amount + partsPrice + startFee;
      } else {
        acc.amount += curr.price * curr.amount + partsPrice;
      }
    }
    
    return acc;
  }, { amount: 0, startFeeApplied: false }).amount;

  return totalWithoutGreen - totalWithGreen;
};

const calculateRut = () => {
  const totalWithRut =
    state.cart.reduce((acc, curr) => {
      return curr.rut ? acc + getItemPrice(curr) : acc;
    }, 0) + (state.cart.some(item => item.rut) ? calculateStartFee().rut : 0);

  const totalWithoutRut = state.cart.reduce((acc, curr) => {
    const partsPrice = curr.parts.reduce((acc, part) => {
      return acc + part.price * part.amount;
    }, 0);
    const startFee = curr.start_fee_obj?.price || 0;

    if (curr.rut) {
      if (!acc.startFeeApplied) {
        acc.startFeeApplied = true;
        acc.amount += curr.price * curr.amount + partsPrice + startFee;
      } else {
        acc.amount += curr.price * curr.amount + partsPrice;
      }
    }
    
    return acc;
  }, { amount: 0, startFeeApplied: false }).amount;

  return totalWithoutRut - totalWithRut;
};

const getTotalPrice = () => {
  return state.cart.reduce((acc, curr) => acc + getItemPrice(curr), 0) + calculateStartFee().rot + calculateStartFee().rut + calculateStartFee().green + calculateStartFee().default;
};

export {
  calculateGreenDiscount,
  calculateRot,
  calculateRut,
  getGreenPrice,
  getItemPrice,
  getPartPrice,
  getProductPrice,
  getProductPriceWithRotAndRut,
  getRotPrice,
  getRotStartFee,
  getRutPrice,
  getRutStartFee,
  getStartFee,
  getTotalPrice,
  groupCartItemsByStartFee,
  calculateStartFee
};
